<template>
<w-sliding-popup ref="slidingPopup" type="setting" :title="title" width="580px" @close="onClose">
  <div slot="content">
    <el-form ref="form" :model="formData" label-position="top" :rules="rules">
      <div class="mr-b25">
        <div class="title-h4 title-bar">기본 정보</div>
        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item label="자원 구분" prop="rsctypeId">
              <el-select v-model="formData.rsctypeId" style="width:100%" :disabled="!this.phantom">
                <el-option v-for="item in rsctypeList" :key="item.rsctypeId" :label="item.rsctypeName" :value="item.rsctypeId"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="자원" prop="hashkeyId">
              <el-select v-model="formData.hashkeyId" style="width:100%" :disabled="!this.phantom">
                <el-option v-for="item in filteredRscList" :key="item.hashkeyIdString" :label="item.rscLabel" :value="item.hashkeyIdString"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item label="예측 일자" prop="predictDatetime">
              <el-date-picker style="width:100%;" v-model="formData.predictDatetime" :disabled="!this.phantom" placeholder="예측 일자" default-time="00:00:00" :picker-options="predictDatePickerOptions" @change="setMinMaxLearnDateByPredictDatetime" />
            </el-form-item>
          </el-col>
        </el-row>
      </div>
      <div>
        <div class="title-h4 title-bar">
          학습 데이터
          <el-tooltip effect="dark" placement="bottom" popper-class="performance-predict-popper">
            <span slot="content">
              예측 기간이 30일 이하인 경우 학습 기간은 <br />
              월간 데이터 보관 기간 ({{ !isEmpty(dataStoragePeriodBasicinfo) ? dataStoragePeriodBasicinfo.month : '' }}개월) 이내에서<br />
              선택할 수 있습니다.
            </span>
            <i class="fas fa-info-circle mr-l4 c-pointer"></i>
          </el-tooltip>
        </div>
        <el-row type="flex" justify="start">
          <el-select style="width:130px;" v-model="learnDateType">
            <el-option v-for="item in learnDateTypes" :key="item.key" :label="item.label" :value="item.key" />
          </el-select>
          <!-- 빠른 시간 선택 -->
          <div v-show="learnDateType === learnDataTypeKeys.QUICK" class="vertical-center-box">
            <span class="mr-r4 mr-l10">최근</span>
            <el-input-number class="mr-r4" style="width:100px;" v-model="quickLearnDayCount" controls-position="right" :min="1" :max="quickMaxCount" />
            <el-select class="mr-r4" style="width:65px;" v-model="quickLearnDayUnit">
              <el-option v-for="item in quickLearnDayUnits" :key="item.key" :label="item.label" :value="item.key" />
            </el-select>
            <el-button :disabled="isNil(quickLearnDayCount)" @click="addQuickLearnData">추가</el-button>
          </div>
          <!-- 사용자 정의 시간 선택 -->
          <div v-show="learnDateType === learnDataTypeKeys.USER_DEFINED" class="vertical-center-box">
            <el-date-picker class="mr-l4 mr-r4" style="width:180px;" v-model="userDefineDates" type="daterange" :start-placeholder="defaultDateRangeOptions.startPlaceholder" :end-placeholder="defaultDateRangeOptions.endPlaceholder" :ranage-separator="defaultDateRangeOptions.rangeSeparator"
              :format="defaultDateRangeOptions.format" :picker-options="pickerOptions" />
            <el-button :disabled="isEmpty(userDefineDates)" @click="addUserDefineLearnData">추가</el-button>
          </div>
        </el-row>
        <hr v-show="!isEmpty(learnDataSet)" class="mr-t10 mr-b5" />
        <el-tag class="mr-r4" v-for="(item, index) in learnDataSet" :key="index" :type="item.duplicate ? 'danger' : 'default'" closable :disable-transitions="true" @close="removeLearnData(index)">
          {{item.label}}
        </el-tag>
      </div>
    </el-form>
  </div>
  <div slot="footer">
    <el-button size="large" type="solid" @click="onSaveButtonClick">저장</el-button>
    <el-button size="large" @click="close">취소</el-button>
  </div>
</w-sliding-popup>
</template>

<script>
import { isNil, isEmpty, clone, forEach, findIndex, filter } from 'lodash';
import moment from 'moment';
import { removeTime, defaultDateRangeOptions } from '@/utils/date';
import predictPerfUtil from '@/utils/predict-performance';
import msgUtil from '@/utils/message';
import EventPredictSettingsApi from '@/api/settings/common/event-predict-settings';

const learnDataTypeKeys = {
  QUICK: 'QUICK',
  USER_DEFINED: 'USER_DEFINED',
};

/**
 * 학습 데이터 설정 팝업
 * @author Ju
 */
export default {
  name: 'WPredictCustomLearnDataSettingPopup',
  data() {
    return {
      obj: null,
      phantom: true, // 신규 학습 데이터 설정 여부. true면 추가 / false면 수정
      formData: {},
      rules: {
        rsctypeId: {
          required: true,
          message: '필수 정보입니다.',
          trigger: 'blur',
        },
        hashkeyId: {
          required: true,
          validator: this.validateHashkeyId,
          trigger: ['blur', 'change'],
        },
        predictDatetime: {
          required: true,
          validator: this.validatePredictDatetime,
          trigger: ['change'],
        },
      },
      rsctypeList: [],
      rscList: [],
      dataStoragePeriodBasicinfo: null, // 데이터 보관 주기 객체
      predictDatePickerOptions: {
        disabledDate: (date) => date <= new Date(),
      },
      predictPeriod: 2,
      predictPeriodUnit: predictPerfUtil.predictPeriodUnitDefine.DAY,
      learnDataTypeKeys,
      learnDateType: learnDataTypeKeys.QUICK, // 학습 날짜 설정 타입
      learnDateTypes: [
        // 학습 날싸 설정 타입 배열
        {
          key: learnDataTypeKeys.QUICK,
          label: '빠른 시간 선택',
        },
        {
          key: learnDataTypeKeys.USER_DEFINED,
          label: '사용자 정의',
        },
      ],
      learnDataSet: [],
      minLearnDate: null, // 최소 설정 가능한 날짜. (예측일자 - 1일 기준으로 계산)
      maxLearnDate: null, // 최대 설정 가능한 날짜. (예측일자 - 1일)
      maxNumberOfLearnDay: null, // 최대 학습 일수
      recommendLearnDayCount: null, // 권장 학습 일수
      /* 빠른 시간 선택 */
      quickLearnDayCount: 12,
      quickMaxCount: 69,
      quickLearnDayUnit: 'd',
      quickLearnDayUnits: [
        {
          key: 'd',
          label: '일',
        },
        {
          key: 'w',
          label: '주',
        },
        {
          key: 'M',
          label: '개월',
        },
      ],
      /* 절대 시간 선택 */
      defaultDateRangeOptions,
      pickerOptions: null,
      userDefineDates: [],
    };
  },
  computed: {
    title() {
      return this.phantom ? '학습 데이터 설정 추가' : '학습 데이터 설정 정보';
    },
    /**
     * 선택한 자원 구분에 해당하는 자원 목록을 제공한다.
     * @author Ju
     * @return {Array}
     */
    filteredRscList() {
      let rscList;
      if (!isNil(this.formData.rsctypeId)) {
        rscList = filter(this.rscList, (item) => item.rsctypeId === this.formData.rsctypeId);
      } else {
        rscList = this.rscList;
      }
      return rscList;
    },
  },
  watch: {
    filteredRscList: {
      handler(to) {
        if (!isEmpty(to) && this.phantom) {
          this.formData.hashkeyId = to[0].hashkeyIdString;
        }
      },
    },
    quickLearnDayUnit: {
      handler(to) {
        if (!isNil(to)) {
          this.setQuickMaxCount();
        }
      },
    },
  },
  created() {
    // 데이터 보관 주기 조회
    predictPerfUtil
      .getDataStoragePeriodBasicinfo()
      .then((result) => {
        if (result.success) {
          this.dataStoragePeriodBasicinfo = result.data;
        } else {
          this.dataStoragePeriodBasicinfo = result.data;
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '데이터 보관 주기 정보', false));
        }
      })
      .catch((result) => {
        this.dataStoragePeriodBasicinfo = result.data;
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '데이터 보관 주기 정보', false));
      })
      .finally(() => {
        // 최대 학습 일수, 권장 학습 일 수 등 설정
        this.maxNumberOfLearnDay = predictPerfUtil.getMaxLearnDayCount(this.predictPeriod, this.predictPeriodUnit);
        this.recommendLearnDayCount = predictPerfUtil.getRecommendLearnDayCount(this.predictPeriod, this.predictPeriodUnit);
        this.pickerOptions = {
          disabledDate: (date) => this.isUnselectableLearnDate(date),
        };
      });
  },
  methods: {
    isNil,
    isEmpty,
    /**
     * 팝업을 연다.
     * @author Ju
     */
    open(obj, customLearnData) {
      this.obj = obj;
      const today = removeTime(new Date());
      if (isNil(customLearnData)) {
        this.phantom = true;
        this.formData = {
          rsctypeId: null,
          hashkeyId: null,
          predictDatetime: moment(today)
            .add(1, 'd')
            .toDate(),
          learnDataSet: null,
          learnDataSetRaw: null,
          learnDataSetLabel: null,
        };
        this.learnDataSet = [];
      } else {
        this.phantom = false;
        const learnDataSet = JSON.parse(customLearnData.learnDataSet);
        if (!isEmpty(learnDataSet)) {
          forEach(learnDataSet, (item) => {
            item.startDatetime = removeTime(moment(item.startDateLabel).toDate());
            item.endDatetime = removeTime(moment(item.endDateLabel).toDate());
          });
        }
        this.learnDataSet = learnDataSet;

        const formData = clone(customLearnData);
        formData.hashkeyId = customLearnData.hashkeyIdString;
        this.formData = formData;
      }
      this.setMinMaxLearnDateByPredictDatetime(this.formData.predictDatetime);
      this.setQuickMaxCount();
      this.$nextTick(() => {
        this.loadPredictTargetRsc();
        this.$refs.slidingPopup.open();
      });
    },
    /**
     * 팝업을 닫는다.
     * @author Ju
     */
    close() {
      this.onClose();
      this.$refs.slidingPopup.close();
    },
    /**
     * 닫기 이벤트 핸들러
     * @author Ju
     */
    onClose() {
      this.$refs.form.resetFields();
      this.learnDateType = learnDataTypeKeys.QUICK;
      this.quickLearnDayCount = 12;
      this.quickLearnDayUnit = 'd';
      this.userDefineDates = [];
      this.learnDataSet = [];
    },
    /**
     * 장비에 설정된 예측 대상 자원 목록을 조회한다.
     * @author Ju
     */
    loadPredictTargetRsc() {
      EventPredictSettingsApi.getPredictTargetRscList(this.obj.objId)
        .then((response) => {
          if (response.data.success) {
            const rscList = response.data.data;
            const rsctypeList = [];
            if (!isEmpty(rscList)) {
              forEach(rscList, (item) => {
                if (findIndex(rsctypeList, (rsctype) => rsctype.rsctypeId === item.rsctypeId) < 0) {
                  rsctypeList.push({
                    rsctypeId: item.rsctypeId,
                    rsctypeName: item.rsctypeName,
                  });
                }
              });
              if (this.phantom) {
                this.formData.rsctypeId = rsctypeList[0].rsctypeId;
              } else {
                const findItem = findIndex(rsctypeList, (item) => this.formData.rsctypeId === item.rsctypeId);
                this.formData.rsctypeId = rsctypeList[findItem].rsctypeId;
              }
            }
            this.rscList = rscList;
            this.rsctypeList = rsctypeList;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '자원 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '자원 정보', false));
          throw new Error(e);
        });
    },
    /**
     * 예측 일자 기준으로 최소/최대 학습 날짜를 설정한다.
     * @author Ju
     * @param  {Date} predictDatetime 예측일자
     */
    setMinMaxLearnDateByPredictDatetime(predictDatetime) {
      this.maxLearnDate = removeTime(
        moment(predictDatetime)
          .subtract(1, 'd')
          .toDate(),
      );
      this.minLearnDate = predictPerfUtil.getMinDateByPredictPeriod(this.dataStoragePeriodBasicinfo, this.maxLearnDate, this.predictPeriod, this.predictPeriodUnit);
    },
    /**
     *  빠른 시간 선택 시 최대 값을 설정한다.
     * 예측 기간 기준 최대 학습 일 수 기준으로 하며,
     * 만약 데이터 보관 주기 기준으로 최소로 선택 가능한 일자와 최대 선택 가능한 일지의 차이가 더 작을 경우 해당 차이를 기준으로 한다.
     * @author Ju
     */
    setQuickMaxCount() {
      if (!isNil(this.formData.predictDatetime) && !isNil(this.minLearnDate) && !isNil(this.maxNumberOfLearnDay)) {
        const baseDatetime = moment(this.predictDatetime).subtract(1, 'd').toDate();
        const diff = moment(baseDatetime).diff(this.minLearnDate, 'd');
        const maxDayCount = this.maxNumberOfLearnDay > diff ? diff : this.maxNumberOfLearnDay;
        const minDatetime = moment(baseDatetime).subtract(maxDayCount, 'd').toDate();

        this.quickMaxCount = moment(baseDatetime).diff(minDatetime, this.quickLearnDayUnit);
        if (this.quickLearnDayCount > this.quickMaxCount) {
          this.quickLearnDayCount = this.quickMaxCount;
        }
      }
    },
    /**
     * 해당 날짜가 최초/최대 학습 날짜 기준으로 선택 가능한 날짜인지 판단한다.
     * @author Ju
     * @param  {Date}  date
     * @return {Boolean} true: 선택불가(비활성화) / false: 선택가능(활성화)
     */
    isUnselectableLearnDate(date) {
      return this.minLearnDate > date || this.maxLearnDate < date;
    },
    /**
     * 빠른 시간 선택 (상대) 기준으로 학습 데이터를 추가한다.
     * @author Ju
     */
    addQuickLearnData() {
      const endDatetime = moment(this.formData.predictDatetime).subtract(1, 'd').toDate();
      const startDatetime = moment(endDatetime)
        .subtract(this.quickLearnDayCount, this.quickLearnDayUnit)
        .add(1, 'd')
        .toDate();
      this.addLearnData(startDatetime, endDatetime);
    },
    /**
     * 사용자 정의 시간 선택 (절대) 기준으로 학습 데이터를 추가한다.
     * @author Ju
     */
    addUserDefineLearnData() {
      this.addLearnData(this.userDefineDates[0], this.userDefineDates[1]);
    },
    /**
     * 학습 데이터를 추가한다.
     * @author Ju
     * @param  {Date} startDatetime 시작날짜
     * @param  {Date} endDatetime   종료날짜
     */
    addLearnData(startDatetime, endDatetime) {
      const duplicateIndex = predictPerfUtil.findOverlapLearnData(this.learnDataSet, startDatetime, endDatetime);
      if (duplicateIndex < 0) {
        if (predictPerfUtil.isExceedLearnDayCount(this.learnDataSet, startDatetime, endDatetime, this.maxNumberOfLearnDay)) {
          this.$message(msgUtil.getCustomMsgData(`학습 데이터는 최대 ${this.maxNumberOfLearnDay}일까지만 추가할 수 있습니다.`));
        } else {
          this.learnDataSet = predictPerfUtil.addLearnData(this.learnDataSet, startDatetime, endDatetime);
        }
      } else {
        this.$message(msgUtil.getCustomMsgData('설정한 기간을 포함하는 학습 기간이 추가되어있습니다.'));
      }
    },
    /**
     * 인덱스에 해당하는 학습 데이터를 삭제한다.
     * @author Ju
     * @param  {[type]} index [description]
     * @return {[type]}       [description]
     */
    removeLearnData(index) {
      this.learnDataSet.splice(index, 1);
      this.learnDataSet = predictPerfUtil.clearDuplicate(this.learnDataSet);
    },
    /**
     * 저장 버튼 클릭 이벤트 핸들러
     * @author Ju
     * @return {[type]} [description]
     */
    onSaveButtonClick() {
      this.$refs.form
        .validate()
        .then((valid) => {
          if (valid) {
            if (isEmpty(this.learnDataSet)) {
              this.$message(msgUtil.getCustomMsgData('학습 데이터를 설정해주십시오.'));
            } else {
              const totalLearnDayCount = predictPerfUtil.getTotalLearnDayCount(this.learnDataSet);
              if (totalLearnDayCount < this.recommendLearnDayCount) {
                this.$confirm(...msgUtil.getCustomConfirmData(`${this.recommendLearnDayCount}일 미만의 학습 데이터로 예측할 경우 정확도가 떨어질 수 있습니다. 저장하시겠습니까?`)).then(() => {
                  this.saveCustomLearnDataSet();
                }).catch(() => {});
              } else {
                this.saveCustomLearnDataSet();
              }
            }
          } else {
            this.$message(msgUtil.getCustomMsgData('값을 확인해주십시오.'));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getCustomMsgData('값을 확인해주십시오.'));
        });
    },
    /**
     * 학습 데이터 설정을 저장한다.
     * @author Ju
     * @return {[type]} [description]
     */
    saveCustomLearnDataSet() {
      const learnDateArray = predictPerfUtil.convertLearnDataSetToDateStringArray(this.learnDataSet);
      const learnDataSetLabel = predictPerfUtil.getLearnDataSetLabel(this.learnDataSet);
      const learnDataSetJson = JSON.stringify(this.learnDataSet);
      const predictStartDatetime = removeTime(this.formData.predictDatetime);
      const predictParams = {
        hashkeyId: this.formData.hashkeyId,
        trainDataList: learnDateArray,
        predictDateTimeInterval: predictPerfUtil.getPreditTimeInterval(this.predictPeriod, this.predictPeriodUnit),
        predictDateTimePeriod: predictPerfUtil.convertToDays(this.predictPeriod, this.predictPeriodUnit),
        predictDateStartTime: moment(predictStartDatetime).format('YYYY-MM-DD HH:mm:ss'),
        isOnDemand: false,
      };
      const data = {
        hashkeyId: predictParams.hashkeyId,
        predictDatetime: predictStartDatetime,
        learnDataSet: learnDataSetJson,
        learnDataSetRaw: JSON.stringify(predictParams),
        learnDataSetLabel,
      };

      const target = '학습 데이터 설정';
      if (this.phantom) {
        const action = msgUtil.action.ADD;
        EventPredictSettingsApi.addPredictCustomLearnData(data)
          .then((response) => {
            if (response.data.success) {
              this.$message(msgUtil.getResultMsgData(action, target, true));
              this.$emit('save');
              this.close();
            } else {
              this.$message(msgUtil.getResultMsgData(action, target, false));
            }
          })
          .catch((e) => {
            this.$message(msgUtil.getResultMsgData(action, target, false));
            throw new Error(e);
          });
      } else {
        const action = msgUtil.action.SAVE;
        EventPredictSettingsApi.updatePredictCustomLearnData(data)
          .then((response) => {
            if (response.data.success) {
              this.$message(msgUtil.getResultMsgData(action, target, true));
              this.$emit('save');
              this.close();
            } else {
              this.$message(msgUtil.getResultMsgData(action, target, false));
            }
          })
          .catch((e) => {
            this.$message(msgUtil.getResultMsgData(action, target, false));
            throw new Error(e);
          });
      }
    },
    /**
     * hashkeyId에 대한 유효성 검사를 한다.
     * @author Ju
     * @param  {[type]}   rule     [description]
     * @param  {[type]}   value    [description]
     * @param  {Function} callback [description]
     * @return {[type]}            [description]
     */
    validateHashkeyId(rule, value, callback) {
      if (isNil(value)) {
        callback(new Error('필수 정보입니다.'));
      } else if (this.phantom && !isNil(this.formData.predictDatetime)) {
        EventPredictSettingsApi.checkDuplicatePredictCustomLearnData(value, this.formData.predictDatetime)
          .then((response) => {
            if (response.data.success) {
              if (response.data.data) {
                callback(new Error('이미 선택한 예측일자에 대한 학습 데이터가 설정된자원입니다.'));
              } else {
                this.$refs.form.clearValidate('predictDatetime');
                callback();
              }
            } else {
              callback(new Error('중복 여부를 확인하지 못하였습니다.'));
            }
          })
          .catch(() => {
            callback(new Error('중복 여부를 확인하지 못하였습니다.'));
          });
      } else {
        callback();
      }
    },
    /**
     * 예측 일자에 대한 유효성 검사를 한다.
     * @author Ju
     * @param  {[type]}   rule     [description]
     * @param  {[type]}   value    [description]
     * @param  {Function} callback [description]
     * @return {[type]}            [description]
     */
    validatePredictDatetime(rule, value, callback) {
      if (isNil(value)) {
        callback(new Error('필수 정보입니다.'));
      } else if (this.phantom && !isNil(this.formData.hashkeyId)) {
        EventPredictSettingsApi.checkDuplicatePredictCustomLearnData(this.formData.hashkeyId, value)
          .then((response) => {
            if (response.data.success) {
              if (response.data.data) {
                callback(new Error('이미 선택한 자원에 대한 학습 데이터가 설정된 예측일자입니다.'));
              } else {
                this.$refs.form.clearValidate('hashkeyId');
                callback();
              }
            } else {
              callback(new Error('중복 여부를 확인하지 못하였습니다.'));
            }
          })
          .catch(() => {
            callback(new Error('중복 여부를 확인하지 못하였습니다.'));
          });
      } else {
        callback();
      }
    },
  },
};
</script>

<style lang="scss">
.performance-predict-popper.el-tooltip__popper {
  max-width: 600px;
}
</style>



// WEBPACK FOOTER //
// src/views/settings/operation/BasicInfoSettings/EventPredictSetting/CustomLearnDataSetting/popup/PredictCustomLearnDataSettingPopup.vue